import { Flex, Button, Box, Image, Square, useBreakpointValue, VStack, Circle, useTheme } from '@telescope/cassini-ui';
import { useWidget } from '@telescope/cassini-hooks';
import { Text, Heading, AdUnit } from 'components';
import { useSpinner } from '../provider';
import { useAuth } from 'features/auth';
import { Spinner } from './Spinner';
import { CiSun } from 'react-icons/ci';
import { SpinTheWheelOpen } from 'types';
import { WHEEL_SIZE } from '../utils';
import { cubicBezier } from 'framer-motion';
import { useSpinToWin } from 'features';

const ANIMATION_DURATION = 10000;
const ANIMATION_OPTIONS = {
  TO_AUTH: {
    duration: ANIMATION_DURATION,
    ease: cubicBezier(.6,.3,.4,1)
  },
  TO_PRIZE: {
    duration: ANIMATION_DURATION,
    ease: cubicBezier(.26,.14,.65,1)
  },
}

export const LandingPage = () => {
  const { isAnimating, isMultiSpin, spinsRemaining, voteLimit, handleSpinToAuth, onAnimationComplete } = useSpinToWin();
  const { prizes, target } = useSpinner();

  const { isAuthorizing, user } = useAuth();
  const { data } = useWidget({ select: (data: SpinTheWheelOpen) => data.snapshot.snapshot_views.wheel });
  const { content, styles, settings } = data!;

  const { data: ads } = useWidget({ select: (data: SpinTheWheelOpen) => data.widget.ads });
  const { wheelCenter } = ads!;

  const adSize = useBreakpointValue(WHEEL_SIZE)!;
  const iconSize = useBreakpointValue({ base: '38px', lg: '45px' })

  const theme = useTheme();
  const { defaultColorScheme: c } = theme.settings;

  const displayRemainingSpins = settings.displayRemainingSpins && isMultiSpin && user;
  const spinsRemainingLabel = content.spinsRemaining[spinsRemaining === 1? 'singular' : 'plural']
    ?.replace('{{COUNT}}', `${spinsRemaining}`)
    .replace('{{TOTAL}}', `${voteLimit}`)

  return (
    <Flex direction="column" align="center" mb={8}>
      <Box textAlign="center" mb={5}>
        <Heading size="2xl" mb={1}>{content.headline}</Heading>
        <Text>{content.description}</Text>
      </Box>

      <VStack spacing={2} mb={5}>
        <Button size={["md", "md", "xl"]} variant="pill" onClick={handleSpinToAuth} 
            isDisabled={isAnimating || isAuthorizing} className="Wheel__spin-button">
              { isMultiSpin && user ? content.spinButton.spinAgain : content.spinButton.default }
        </Button>
        { displayRemainingSpins && <Text fontSize="xs"> {spinsRemainingLabel} </Text> }
      </VStack>
      
      <Flex direction="column" align="center">
        <CiSun size={iconSize}/>
        { isAuthorizing? 
          <Circle opacity={0.3} filter="blur(1rem)" bg={`${c}.500`} size={{...WHEEL_SIZE}} /> :
          <Spinner items={prizes} target={target} animate={isAnimating} 
                 onComplete={user? () => onAnimationComplete() : undefined} 
                 animationOptions={user? ANIMATION_OPTIONS.TO_PRIZE : ANIMATION_OPTIONS.TO_AUTH} 
                 styles={styles}>

            <CenterImage {...content.centerImage} />
            <AdUnit width={adSize} height={adSize} alignSelf="center" my={[2, 5]} {...wheelCenter.custom} />
          </Spinner>  }
      </Flex>
      
    </Flex>
  )
}

const CenterImage = ({ src, alt }: SpinTheWheelOpen['snapshot']['snapshot_views']['wheel']['content']['centerImage']) => {
  if (!src) return null;

  return (
    <Square size={WHEEL_SIZE}>
      <Image w="100%" h="100%" src={src} alt={alt} />
    </Square>
  )
}